import type { BlogsPageData, PageProps } from "@/types";
import React, { FC, useRef } from "react";
import { Link } from "../link";
import { ResourcesCta, ResourcesFeaturedPost } from "../resources";
import { PostCard } from "../post-card";
import SplitTextAnimation from "../split-text-animation";
import { useInView } from "@/hooks/reveal-in-view";

export const Blogs: FC<PageProps<BlogsPageData>> = (props) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });
  const [visibleBlogsCount, setVisibleBlogsCount] = React.useState(6);
  const totalBlogsCount = props.data.blogs.length;
  const restElementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      const element = restElementRef.current;
      if (element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;
        if (
          rect.bottom <= viewportHeight &&
          visibleBlogsCount < totalBlogsCount
        ) {
          setVisibleBlogsCount((prevCount) =>
            Math.min(prevCount + 6, totalBlogsCount)
          );
        }
      }
    };

    const element = restElementRef.current;
    if (element) {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (element) {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [visibleBlogsCount, totalBlogsCount]);

  return (
    <div>
      <div className="wrapper">
        <div className="resources--links">
          {props.meta.translation.header.resources.children.map(
            (item, itemIdx) => {
              return (
                <Link
                  key={itemIdx}
                  noStyle
                  href={item.link?.uri || "#"}
                  className="resources--link"
                >
                  {item.text}
                </Link>
              );
            }
          )}
        </div>
      </div>
      <ResourcesFeaturedPost
        post={props.data.featuredPost}
        readMore={props.meta.translation.resources.readMore}
      />
      {props.data.blogs.length > 0 && (
        <div className="resourcesPage--latest">
          <div ref={ref} className="wrapper">
            <SplitTextAnimation
              htmlElement={
                <h2 className="resourcesPage--title">Latest Blogs</h2>
              }
              isInView={isInView}
            />
            <div className="resourcesPage--list">
              {props.data.blogs.slice(0, 4).map((blog, index) => {
                return (
                  <PostCard
                    key={index}
                    card={blog}
                    theme="dark"
                    readMore={props.meta.translation.resources.readMore}
                    imagePriority={index < 2}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="wrapper wrapper_lg">
        <ResourcesCta
          title={"Sign up for the newsletter to stay in the know"}
        />
      </div>
      {props.data.blogs.length > 4 && (
        <div className="resourcesPage--rest" ref={restElementRef}>
          <div className="wrapper">
            <div className="resourcesPage--list">
              {props.data.blogs
                .slice(0, visibleBlogsCount)
                .map((blog, index) => {
                  return (
                    <PostCard
                      key={index}
                      card={blog}
                      theme="dark"
                      readMore={props.meta.translation.resources.readMore}
                    />
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
